<template>
  <form-item
    :class="['ui-form-item--select', { 'ui-form-item--opened': opened }]"
    :label="label" @click.stop="opened = !opened">
    <div class="ui-form-item__select__value">{{ currentValue }}</div>
    <div class="ui-form-item__select__items">
      <div :class="itemClass(item.value)" v-for="item in items"
           :key="item.value" @click.stop="selectItem(item.value)">
        <icon-check class="ui-form-item__select__check"/>
        {{ item.title }}
      </div>
    </div>
  </form-item>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, PropType, ref,
} from 'vue'
import FormItem from '@/vue/ui/form/FormItem.vue'
import IconCheck from '@/vue/icons/IconCheck.vue'
import { useI18n } from 'vue-i18n'

type selectValue = string | number | boolean

export interface SelectItem {
  value: selectValue
  title: string
}

export default defineComponent({
  name: 'FormSelect',
  props: {
    modelValue: [String, Number, Boolean, Array] as PropType<selectValue | selectValue[]>,
    items: {
      type: Array as PropType<SelectItem[]>,
      required: true,
    },
    label: String as PropType<string>,
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  components: {
    IconCheck,
    FormItem,
  },
  setup(props, { emit }) {
    const { t } = useI18n()

    const value = computed<selectValue | selectValue[]>({
      get: () => (props.modelValue === undefined || props.modelValue === null ? '' : props.modelValue),
      set: (v) => emit('update:modelValue', v),
    })
    const currentValue = computed(() => {
      const values = props.items.filter((i: SelectItem) => {
        if (props.multiple) {
          return (value.value as selectValue[]).includes(i.value)
        }
        return value.value === i.value
      }).map((i: SelectItem) => i.title)
      return `${values.length > 1 ? `(${values.length}) ` : ''}${values.length ? values.join(', ') : t('admin.ui.notChecked')}`
    })

    const opened = ref(false)

    const selectItem = (v: selectValue) => {
      if (props.multiple) {
        const val = [...(value.value as selectValue[])]
        const index = val.indexOf(v)
        if (index >= 0) {
          val.splice(index, 1)
        } else {
          val.push(v)
        }
        value.value = val
      } else {
        value.value = v
        opened.value = false
      }
    }

    const itemClass = (v: selectValue) => [
      'ui-form-item__select__item',
      {
        'ui-form-item__select__item--selected': props.multiple ? (value.value as selectValue[]).includes(v) : v === value.value,
      },
    ]

    const closeItems = () => {
      opened.value = false
    }
    document.addEventListener('click', closeItems, { passive: true })
    onBeforeUnmount(() => document.removeEventListener('click', closeItems))

    return {
      value,
      currentValue,
      opened,
      selectItem,
      itemClass,
    }
  },
})
</script>

<style lang="stylus">
.ui-form-item--select .ui-form-item__block
  position relative

.ui-form-item
  &__select
    &__value
      width 100%
      border 1px solid var(--border-gray-color)
      border-radius .25rem
      height 38px
      display flex
      align-items center
      white-space nowrap
      overflow hidden
      text-overflow ellipsis
      padding .375rem .75rem
      cursor pointer

    &__items
      position absolute
      top 40px
      left 0
      padding 3px 0
      background #fff
      box-shadow 0 0 5px rgba(0, 0, 0, .3)
      width 100%
      border-radius .25rem
      z-index 5
      max-height 200px
      overflow auto
      display none

    &__check
      display none

    &__item
      padding 2px 5px 2px 30px
      cursor pointer
      position relative

      &:hover
        background var(--light-dark-color)

      &--selected .ui-form-item__select__check
        display block
        position absolute
        left 7px
        top 5px

  &--opened .ui-form-item__select__items
    display block
</style>
